import React, { createContext, useState, useContext, useCallback } from 'react';
import { Modal, message } from 'antd'; // Import Modal for prompts

const PatientChartContext = createContext();

export const usePatientChart = () => useContext(PatientChartContext);

const MAX_OPEN_CHARTS = 1; // Changed from 3 to 1

export const PatientChartProvider = ({ children }) => {
  const [openCharts, setOpenCharts] = useState([]); // Array of { id: string, name: string }
  const [activeChartId, setActiveChartId] = useState(null);
  const [isChartVisible, setIsChartVisible] = useState(false); // Control chart visibility
  const [vitalsRefreshTrigger, setVitalsRefreshTrigger] = useState(0); // Trigger for vitals refresh
  const [patientListRefreshTrigger, setPatientListRefreshTrigger] = useState(0); // Trigger for patient list refresh
  const [chartAction, setChartAction] = useState(null); // Trigger for specific chart actions

  // Function to show the chart
  const showChart = useCallback(() => {
    setIsChartVisible(true);
  }, []);

  // Function to hide the chart
  const hideChart = useCallback(() => {
    setIsChartVisible(false);
  }, []);

  // Function to add a new chart tab
  const addChart = useCallback((patient) => {
    if (!patient || !patient._id || !patient.firstName || !patient.lastName) {
      console.error("Invalid patient data provided to addChart");
      message.error("Could not open chart: Invalid patient data.");
      return;
    }

    const chartData = {
      id: patient._id,
      name: `${patient.lastName}, ${patient.firstName}`, // Keep for display consistency if needed elsewhere
      firstName: patient.firstName,
      middleName: patient.middleName, // Add middleName field
      lastName: patient.lastName,
      dateOfBirth: patient.dateOfBirth,
      sex: patient.sex
    };

    // If the requested chart is already the active one and visible, do nothing further.
    // If it's active but not visible, just make it visible.
    if (activeChartId === chartData.id) {
      if (!isChartVisible) {
        setIsChartVisible(true);
      }
      return;
    }
    
    // Since MAX_OPEN_CHARTS is 1, opening a new chart always means replacing the current one (if any).
    setOpenCharts([chartData]); // Set the openCharts array to contain only the new chart
    setActiveChartId(chartData.id);
    setIsChartVisible(true); // Show the chart

  }, [activeChartId, isChartVisible]); // Adjusted dependencies

  // Function to remove/close the currently active chart
  const removeChart = useCallback((targetId) => {
    // Only proceed if the targetId is the currently active chart and there's one open
    if (targetId === activeChartId && openCharts.length === 1 && openCharts[0].id === targetId) {
      setOpenCharts([]); // Clear the open charts
      setActiveChartId(null); // No active chart
      setIsChartVisible(false); // Hide the chart drawer
    }
    // If targetId is not the active chart, or multiple charts are somehow open (which shouldn't happen with MAX_OPEN_CHARTS = 1),
    // this logic might need adjustment, but for a single chart model, this is primary.
  }, [openCharts, activeChartId]); // Dependencies: openCharts, activeChartId



  // Function to trigger vitals refresh
  const refreshVitals = useCallback(() => {
    setVitalsRefreshTrigger(prev => prev + 1);
  }, []);

  // Function to trigger patient list refresh
  const refreshPatientList = useCallback(() => {
    setPatientListRefreshTrigger(prev => prev + 1);
  }, []);

  // Function to trigger specific chart actions
  const triggerChartAction = useCallback((action) => {
    setChartAction(action);
  }, []);

  // Function to clear chart action (called by components after handling the action)
  const clearChartAction = useCallback(() => {
    setChartAction(null);
  }, []);

  const value = {
    openCharts,
    activeChartId,
    isChartVisible,
    vitalsRefreshTrigger,
    patientListRefreshTrigger,
    chartAction,
    addChart,
    removeChart,
    showChart,
    hideChart,
    refreshVitals,
    refreshPatientList,
    triggerChartAction,
    clearChartAction,
  };

  return (
    <PatientChartContext.Provider value={value}>
      {children}
    </PatientChartContext.Provider>
  );
};
